import React from 'react'
import withRedux from 'next-redux-wrapper'
import Theme from 'components/Theme'
import parse from 'lib/url/parse'
import { Actions as PageActions } from 'actions/page'
import { App, Store } from 'apps/public/profile/artistProfile'
import { storeSetup } from 'lib/pageSetup'
import { convertLocaleToDefaultCurrency } from 'components/FormatPrice/helpers'
import {
  getArtistDataLayer,
  getCuratorDataLayer,
  getProfileMetaData,
  getAllArtworksMetaData,
  getAllArtworksDataLayer,
  getUserName,
} from 'apps/public/profile/artistProfile/selectors'
import { browseBooleanFacets as artworkSearchBrowseBooleanFacets } from 'lib/browse'
import {
  parseUrlFacets as artworkSearchParseUrlFacets,
  isSafeRedirectUrl,
} from 'components/SA/SearchProvider/libs/helpers/url'
import { getSlugifyDictionary as getConstructorSlugifyDictionary } from 'selectors/searchProvider'
import { CURATOR } from 'lib/user/constants'
import { getPathParams, getPageParams } from 'apps/user/artworks/selectors/app'
import { getPageParamsQuery } from 'apps/user/artworks/helpers'
import { getMeasurementUnit, getUserID } from 'selectors/user/session'
import { SearchProviderActions } from 'actions/searchProvider'
import { SAFacets } from 'types/SearchProvider'
import { SearchApi } from 'components/SA/SearchProvider/types'
import { ConstructorSearchService } from 'components/SA/SearchProvider/services/constructor/constructorService'
import { getUserPreferencesDestinationZone } from 'selectors/user/cookie'
import { getSortValues } from 'components/SA/SearchProvider/helpers'
import { getAccountUserID } from 'apps/public/profile/artistProfile/selectors'
const { ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_API } = process.env
import { DEFAULT_ALL_ARTWORKS_SORT } from 'apps/user/artworks/helpers/constants'
export const applicationId: string = ALGOLIA_APPLICATION_ID || ''
export const searchApi: string = ALGOLIA_SEARCH_API || ''

const constructorClientSideTransition = async ({ store, query }) => {
  store.dispatch(PageActions.IS_READY(false))
  const { href } = window.location
  const { section } = query
  const pageQueryParams = getPageParamsQuery(href)
  const { hitsPerPage, page, query: searchQuery, sort } = pageQueryParams
  const state = { ...store.getState() }
  const destinationZone = getUserPreferencesDestinationZone(state)
  const slugifyDictionary = getConstructorSlugifyDictionary(state)
  const pathParams = getPathParams(state)
  const pageParams = getPageParams(state)
  const artistId = getAccountUserID(state)
  const userSizePreference = getMeasurementUnit(state)
  const userId = getUserID(state)
  const selectedFacets = artworkSearchParseUrlFacets({
    dictionary: slugifyDictionary,
    href,
    routeParams: pathParams,
    booleanFacets: artworkSearchBrowseBooleanFacets,
  })
  const selectedCategory = selectedFacets.category?.[0]

  // We only want AVAIL => FOR SALE artworks - Manually add query, Ignore Prints
  const predefinedFilters = { artist_id: [artistId] }
  const hasSearchQuery = Boolean(searchQuery)
  const { sortBy, sortOrder } = getSortValues({ sort, destinationZone })
  const isSpecificCategorySelected = Boolean(selectedCategory) && selectedCategory !== 'all'
  const shouldAddGroupIdFilter = hasSearchQuery && isSpecificCategorySelected
  const searchService: SearchApi = new ConstructorSearchService({ userId: String(userId) })
  const { success, payload } = await searchService.fetchSearchProviderData({
    query: searchQuery,
    filters: selectedFacets,
    predefinedFilters,
    sort,
    page,
    resultsPerPage: hitsPerPage,
    destinationZone,
    selectedCategory,
    isPrintsPage: false,
    userSizePreference,
    shouldAddGroupIdFilter: false,
    defaultSortObject: DEFAULT_ALL_ARTWORKS_SORT,
  })

  const { results, facets, totalResults } =
    success && payload.data.type === 'products'
      ? payload.data
      : {
          results: [],
          facets: ([] as unknown) as SAFacets,
          totalResults: 0,
        }

  // Let's create a LIST of ACTIONS to dispatch
  const storeActions = [
    SearchProviderActions.UPDATE_SEARCH_PROVIDER({
      results,
      facets,
      selectedFacets,
      pagination: {
        totalResults: Number(totalResults),
        resultsPerPage: Number(hitsPerPage),
        pageNumber: Number(page),
      },
      sort: {
        sortBy,
        sortOrder,
      },
    }),
    PageActions.SET({
      path: parse(href).path || '',
      params: {
        ...pageParams,
        ...pageQueryParams,
        userId: artistId,
        section: section ? section : 'artworks',
      },
    }),
    PageActions.IS_READY(true),
  ]
  // Dispatch Array<ACTIONS>
  store.dispatch(storeActions)
}

const constructorGetInitialProps = async ({ req, res, query, store }) => {
  const { destinationZone, location, localeSSR, cookies, localeAvailableCurrencies } = req
  const isObjectString =
    typeof cookies?.saatchi_user_preferences === 'string' &&
    cookies?.saatchi_user_preferences[0] === '{'
  const preferencesParsed = isObjectString
    ? JSON.parse(cookies?.saatchi_user_preferences || '{}')
    : {}
  const userId = '' // we don't have userid on get initial props
  const userCurrencyPreferences = preferencesParsed?.currency || ''
  const selectedCurrency = localeAvailableCurrencies?.[userCurrencyPreferences]
  const userSizePreference = localeSSR?.measurementUnit
  const { searchProviderSetup, page } = query
  const {
    data: { pathParams },
  } = page
  const { hitsPerPage, page: pageNumber, query: searchQuery, sort: sortQueryParam } = page?.params
  // Configuration Data
  const { facets, sharedFacetNames, slugifyDictionary } = searchProviderSetup
  const predefinedFilters = { artist_id: [req.accountData.userId] }
  // Request with no results just to get the total number of results for metadata
  // Create Facets List for Redux Store, we will merge in URL Params & Queries ( slugify )
  const selectedFacets = artworkSearchParseUrlFacets({
    dictionary: slugifyDictionary,
    href: location.href,
    routeParams: pathParams,
    booleanFacets: artworkSearchBrowseBooleanFacets,
  })
  // Just to fix the geo_price value sent to Constructor if the user changes the currency preference (just here because the page always reloads)
  if (
    selectedFacets?.['custom__geo_prices']?.length > 0 &&
    selectedFacets?.['geo_prices']?.length > 0
  ) {
    const splitCustomPrice = selectedFacets?.['custom__geo_prices']?.[0]?.split('-')
    const price = splitCustomPrice.map((value) =>
      convertLocaleToDefaultCurrency(parseInt(value, 10), selectedCurrency)
    )
    const priceString = price.join('-')
    selectedFacets['geo_prices'] = [priceString]
  }
  const { sortBy, sortOrder } = getSortValues({ sort: sortQueryParam, destinationZone })
  // Request with no results just to get the total number of results for metadata
  const selectedCategory = selectedFacets.category?.[0]
  const hasSearchQuery = Boolean(searchQuery)
  const isSpecificCategorySelected = Boolean(selectedCategory) && selectedCategory !== 'all'
  const shouldAddGroupIdFilter = hasSearchQuery && isSpecificCategorySelected
  const searchService: SearchApi = new ConstructorSearchService({ userId: String(userId) })
  const { success, payload } = await searchService.fetchSearchProviderData({
    query: searchQuery,
    filters: selectedFacets,
    predefinedFilters,
    sort: sortQueryParam,
    page: 1,
    resultsPerPage: 1,
    destinationZone,
    selectedCategory,
    isServerSide: true,
    isPrintsPage: false,
    userSizePreference,
    shouldAddGroupIdFilter: false,
    defaultSortObject: DEFAULT_ALL_ARTWORKS_SORT,
  })

  const dataReponse = success ? payload.data : { type: 'error', totalResults: 0 }
  const totalResults = dataReponse?.type === 'products' ? dataReponse.totalResults : 0

  const searchProviderData = {
    facets,
    sharedFacetNames,
    slugifyDictionary,
    selectedFacets,
    pagination: {
      resultsPerPage: Number(hitsPerPage),
      pageNumber: Number(pageNumber),
      totalResults,
    },
    sort: {
      sortBy,
      sortOrder,
    },
    isPrintsPage: false,
  }

  store.dispatch(SearchProviderActions.UPDATE_SEARCH_PROVIDER(searchProviderData))
}
function Page() {
  return (
    <Theme>
      <App />
    </Theme>
  )
}

Page.clientSideTransitions = async ({ store, query }) => {
  if (window) {
    await constructorClientSideTransition({ store, query })
    const { section } = query
    // Data Layer
    const dataLayer =
      section === 'artworks'
        ? getAllArtworksDataLayer({ ...store.getState() })
        : getArtistDataLayer({ ...store.getState() })
    window.dataLayer.push({ ...dataLayer, event: 'pageview' })
  }
}

Page.getInitialProps = async ({ req, store, res, query }) => {
  // Client-Side
  if (!req) {
    await Page.clientSideTransitions({
      store,
      query,
    })
    const { section } = query
    const state = { ...store.getState() }
    const metaData =
      section === 'artworks' ? getAllArtworksMetaData(state) : getProfileMetaData(state)
    return {
      metaData,
    }
  }

  // SSR
  storeSetup(store, req)
  const { originalUrl, accountData, accountType, query: reqQuery } = req
  const { params } = req
  const { section } = params
  if (section === 'artworks') {
    await constructorGetInitialProps({ req, res, query, store })
  }

  const pageData = {
    data: {
      accountData,
      accountType,
    },
    name: '/public/profile/artistProfile',
    params: { ...params, ...reqQuery },
    path: originalUrl,
  }
  // Page Data Setup
  store.dispatch(PageActions.SET(pageData))

  // Akamai Cache Tag
  const { userId } = query
  accountType === CURATOR
    ? res.set(
        'Edge-Cache-Tag',
        `page-curatorProfile, userId-${userId}, page-curatorProfile-${userId}`
      )
    : res.set('Edge-Cache-Tag', `page-userProfile, userId-${userId}, page-userProfile-${userId}`)
  // Metadata & Datalayer
  const state = { ...store.getState() }
  const metaData =
    section === 'artworks' ? getAllArtworksMetaData(state) : getProfileMetaData(state)
  const dataLayer =
    section === 'artworks'
      ? getAllArtworksDataLayer(state)
      : accountType === CURATOR
      ? getCuratorDataLayer(state)
      : getArtistDataLayer(state)
  return {
    dataLayer,
    metaData,
  }
}

export default withRedux(Store)(Page)
